import { CONTROL_POINT_COLORS } from "./controleTheme";
import { PATIENT_CONTROL_POINT_KEYS, isPatientControlValidated, normalizePatientPointState } from "./patientControlRules";

// Les écrans Contrôle (service dynamique) et Contrôle Bis (planning robot) suivent la même check-list ;
// une variante ne porte que leurs différences de libellés.
const BASE_SECTIONS = [
    {
        key: "avantLancement",
        title: "Check list avant lancement production robot PDA",
        rows: [
            ["importEureka", "Import des données via Eureka"],
            ["tracageLogiciels", "Traçage validation pharmaceutique et logiciels de soin"],
            ["datesProduction", "Vérification des dates de production"],
            ["posologies", "Vérification des posologies"],
            ["fspMduHp", "Vérification FSP, MDU et hors pilulier"],
            ["besoinsRemplissage", "Import du tableau des besoins de remplissage cassette"],
            ["rappelsProduction", "Rappels production, patients absents et modifications de traitements"],
            ["ruptures", "Validation des ruptures de médicament"],
            ["sortiesCassettes", "Sortie des médicaments arrêtés ou rompus des cassettes"],
            ["retraitsLots", "Validation des retraits de lots"],
        ],
    },
    {
        key: "nonConformites",
        title: "Fiche de signalement de non-conformité",
        rows: [
            ["comprimesDecales", "Comprimés/gélules décalés"],
            ["integriteMedicament", "Perte d'intégrité du médicament"],
            ["quantiteInexacte", "Quantité inexacte de comprimé/gélule"],
            ["elementEtranger", "Élément étranger"],
            ["aspectPilulier", "Aspect non conforme du pilulier"],
            ["erreurPlateau", "Erreur plateau FSP/MDU"],
            ["panneBloquante", "Panne bloquante"],
            ["erreurRemplissage", "Erreur de remplissage"],
            ["erreurManipulation", "Erreur de manipulation"],
            ["problemeInformatique", "Problème informatique"],
        ],
    },
    {
        key: "controleProduction",
        title: "Contrôle en cours de production",
        rows: [
            ["temperatureSoudure", "Température de soudure", "80-85°C"],
            ["humiditeAir", "Humidité de l'air ambiant", "30-40%"],
            ["scellageSachets", "Scellage des sachets", "Étanchéité assurée"],
            ["controleVisuel", "Contrôle visuel", "Conforme"],
            ["conditionnementEtiquetage", "Conditionnement et étiquetage", "Correctement aligné"],
        ],
    },
    {
        key: "liberationLot",
        title: "Fiche de libération de lots",
        rows: [
            ["dateDebutTraitement", "Date de début de traitement"],
            ["patientLisible", "Nom et prénom patient lisible"],
            ["tracabiliteOrdonnance", "Traçabilité ordonnance"],
            ["productionSachet", "Production sachet"],
            ["nomEhpad", "Nom de l'EHPAD"],
            ["nombreSachet", "Nombre de sachets"],
            ["descriptionMedicaments", "Description des médicaments"],
        ],
    },
    {
        key: "rendement",
        title: "Rendement",
        rows: [
            ["patientsProduits", "Patients produits"],
            ["patientsCaisseLivraison", "Patients mis en caisse livraison"],
            ["residentsEhpad", "Nombre de résidents EHPAD"],
        ],
    },
    {
        key: "extractionHp",
        title: "Extraction des HP",
        rows: [],
    },
];

const TRACAGE_BY_CONTROLE_MODE = {
    NETSOIN: "Traçage validation pharmaceutique Netsoin",
    TITAN: "Traçage validation pharmaceutique pour Titan",
    DOMINLIK_IMAGO: "Vérification du traçage des patients et des EHPAD non interopérables via Dominlik et Imago",
    SANS_LOGICIEL: "Vérification EHPAD sans logiciel de soin directement sur Eureka et ordo LGPI",
};

// Champs relus puis renvoyés quand un dossier de contrôle modifie la fiche administrative d'un patient. Le serveur
// remplace toute la fiche : un champ absent serait effacé, donc les deux écrans de contrôle envoient la liste complète.
export const PATIENT_EDITABLE_FIELDS = [
    "version", "nom", "prenom", "dateNaissance", "chambre", "productionSubcategory", "patientStatus", "nir", "inactive", "notInvoiced",
    "creditAuthorizationType", "creditAuthorizationAmount", "creditAuthorization", "amoStatus",
    "amcDate", "amcScanReceived", "scanAuto", "creditNote", "guardianContactStatus", "guardianContactValue",
    "guardianSentDate", "correspondingPharmacistDeclaration", "administrativeRemarks",
    "admissionAssessmentDate", "admissionInterviewDate", "admissionInterviewNote", "admissionMembershipDate",
    "pdaProductionRemark", "antalgique", "bpmInitialInterview", "bpmYear1TreatmentAnalysis",
    "bpmYear1FollowUpInterview", "bpmYear1ObservanceInterview", "insulin", "authorizedInvoiceCount", "zopiclone",
    "expensiveProducts", "chronicLastBillingDate", "billingRemarks", "toInvoice",
];

function withPreLaunchLabels(sections, labels) {
    return sections.map(section => (
        section.key === "avantLancement"
            ? { ...section, rows: section.rows.map(([key, label, ...rest]) => [key, labels[key] || label, ...rest]) }
            : section
    ));
}

export const CLASSIC_VARIANT = {
    sections: BASE_SECTIONS,
    patientControlInstructions: {
        tracageLogiciels: "Vérifier le traçage de la validation pharmaceutique et la cohérence avec les logiciels de soin.",
        datesProduction: "Sur Eureka Vérification des dates de production : S'assurer que les dates indiquées sont correctes et couvrent l'ensemble de la semaine pour les chroniques (sinon il faut renouveler traitement sur logiciel pour les chroniques):",
        posologies: "Sur Eureka Vérification des Posologies : S'assurer que les posologies indiquées sont correctes et à jour:",
    },
    tracageByControleMode: TRACAGE_BY_CONTROLE_MODE,
    launchStatusLabels: { LANCE: "Lancé", PAS_LANCE: "Pas lancé" },
};

export const PRODUCTION_VARIANT = {
    sections: withPreLaunchLabels(BASE_SECTIONS, {
        importEureka: "Import des données via planificateur",
        tracageLogiciels: "Vérification du traçage de la validation pharmaceutique selon le logiciel de soin EHPAD ou le planificateur",
        datesProduction: "Vérification des dates de production (Validation durée de prescription)",
        rappelsProduction: "Validation des retraits de lot",
    }),
    patientControlInstructions: {
        tracageLogiciels: CLASSIC_VARIANT.patientControlInstructions.tracageLogiciels,
        datesProduction: "Sur planificateur Vérification des dates de production : S'assurer que les dates indiquées sont correctes et couvrent l'ensemble de la semaine pour les chroniques (sinon il faut renouveler traitement sur logiciel pour les chroniques):",
        posologies: "Sur planificateur Vérification des Posologies : S'assurer que les posologies indiquées sont correctes et à jour:",
    },
    tracageByControleMode: {},
    launchStatusLabels: { LANCE: "Validé", PAS_LANCE: "Pas validé" },
};

export function patientControlPoints(variant) {
    return variant.sections[0].rows.filter(([key]) => PATIENT_CONTROL_POINT_KEYS.includes(key));
}

export function hasUnvalidatedPatientPoints(value) {
    if (!isPatientControlValidated(value)) return false;
    return PATIENT_CONTROL_POINT_KEYS.some(pointKey => !normalizePatientPointState(value?.[pointKey]).validated);
}

export function controlPointLabel(variant, sectionKey, rowKey, label, controleMode) {
    if (sectionKey === "avantLancement" && rowKey === "tracageLogiciels") {
        return variant.tracageByControleMode[controleMode] || label;
    }
    return label;
}

export function controlPointNumber(variant, sectionIndex, rowIndex) {
    return variant.sections
        .slice(0, sectionIndex)
        .reduce((total, section) => total + section.rows.length, rowIndex + 1);
}

export function controlPointColor(number) {
    return CONTROL_POINT_COLORS[(number - 1) % CONTROL_POINT_COLORS.length];
}

export function launchStatusLabel(variant, value) {
    return value === "LANCE" ? variant.launchStatusLabels.LANCE : variant.launchStatusLabels.PAS_LANCE;
}
